import * as React from "react";

import {
  faFilter,
  faCalendarDays,
  faFilterSlash,
  faSearch,
  faColumns,
} from "@fortawesome/sharp-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  BackendFailureAlert,
  Box,
  ControlBar,
  DataGrid7 as DataGrid,
  GridOverlay,
  LinearProgress,
  Typography,
  Stack,
  GridToolbarQuickFilter,
  styled,
  GridRenderCellParams,
  GridColDef,
} from "@platform/style";

import { TemplateV2 } from "../../../models/TemplateV2";
import { memo, useCallback } from "react";

library.add(faFilter, faCalendarDays, faFilterSlash, faSearch, faColumns);

interface WorkTemplateProps {
  onRowSelect: (arg0: TemplateV2) => void;
  isLoadingTemplates: boolean;
  isErrorTemplates: boolean;
  isLoadingBusinessProcesses: boolean;
  isErrorBusinessProcesses: boolean;
  refetchBusinessProcesses: () => void;
  refetchTemplates: () => void;
  templates: TemplateV2[];
}

const WorkTemplateGrid = memo((props: WorkTemplateProps) => {
  const handleRowSelect = useCallback((params: { row: TemplateV2 }) => {
    props.onRowSelect(params.row);
  }, []);

  function CustomNoRowsOverlay() {
    return (
      <GridOverlay>
        <Typography>No Templates to Display.</Typography>
      </GridOverlay>
    );
  }

  function QuickSearchToolbar() {
    return (
      <ControlBar
        leftSlotComponents={
          <>
            <GridToolbarQuickFilter
              placeholder={"Search (i.e. Escrow, Billing)"}
              size={"small"}
              quickFilterParser={(searchInput: string) =>
                searchInput
                  .split(",")
                  .map((value) => value.trim())
                  .filter((value) => value !== "")
              }
            />
          </>
        }
      ></ControlBar>
    );
  }

  const StyledDataGrid = styled(DataGrid)(() => ({
    "& .MuiDataGrid-filterIcon": {
      color: "white",
    },
  }));

  const columns: GridColDef[] = [
    {
      field: "BusinessArea",
      headerName: "Business Area",
      description: "Business Area",
      flex: 0.92,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "BusinessFunction",
      headerName: "Business Function",
      description: "Business Function",
      flex: 1.0,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "BusinessProcess",
      headerName: "Business Process",
      description: "Business Process",
      flex: 1.0,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "Title",
      headerName: "Title",
      description: "Template Title",
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "TemplateCode",
      headerName: "Code",
      description: "Template Code",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "Description",
      headerName: "Description",
      description: "Description",
      flex: 0.75,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "ServiceLevelInDays",
      headerName: "SLA",
      description: "Service Level In Days",
      flex: 0.45,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (params.value == 1 ? params.value + " Day" : params.value + " Days") : "0",
    },
    {
      field: "Priority",
      headerName: "Priority",
      description: "Priority",
      flex: 0.55,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "WorkGroups",
      headerName: "Groups",
      description: "Groups",
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) =>
        params.value && params.value.length > 0 ? params.value.length : 0,
    },
    {
      field: "EnabledIndicator",
      headerName: "Activity",
      description: "Activity",
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) => (params.value == true ? "Active" : "Inactive"),
    },
  ];

  return (
    <>
      <Stack>
        <Box sx={{ height: "50vh", width: "100%" }}>
          {props.isLoadingTemplates || props.isLoadingBusinessProcesses ? (
            <LinearProgress sx={{ mt: 1 }} />
          ) : (
            <Box sx={{ mt: 1, height: "4px" }}></Box>
          )}
          {props.isErrorTemplates || props.isErrorBusinessProcesses ? (
            <Box sx={{ mt: 1 }}>
              <BackendFailureAlert
                retryFunction={
                  props.isErrorTemplates ? props.refetchTemplates : props.refetchBusinessProcesses
                }
              />
            </Box>
          ) : (
            <StyledDataGrid
              hideFooter={true}
              rows={props.templates}
              rowCount={props.templates.length}
              getRowId={(row: { TemplateCode: string }) => row.TemplateCode}
              rowHeight={60}
              columns={columns}
              data-testid={"DataGrid"}
              slots={{
                toolbar: QuickSearchToolbar,
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              onRowClick={handleRowSelect}
            />
          )}
        </Box>
      </Stack>
    </>
  );
});

export default WorkTemplateGrid;
